<script setup>
import { ref, watch, inject } from 'vue';
import useDialog from '@/composables/use-dialog';
import useCustomForm from '@/composables/use-CustomForm';
import CreationDialog from '@/components/widgets/creationDialog/CreationDialog.vue';
import passwordFormTemplate from '@/config/templates/passwordForm';
import FormSectionTitle from '@/components/widgets/forms/FormSectionTitle.vue';
import FormLine from '@/components/widgets/creationDialog/FormLine.vue';
import ErrorMessage from '@/components/widgets/ErrorMessage.vue';
import RetryFailedRequest from '@/components/widgets/RetryFailedRequest.vue';

const { show, close } = useDialog();
const backendApi = inject('backendApi');
defineProps(['title']);
const emit = defineEmits('saved');

const { fieldModels, messageErrors, onValidatedSubmit, clearForm } =
    useCustomForm(passwordFormTemplate);

//  Visibility of the section of the form
const sectionVisibilities = ref(passwordFormTemplate.map(() => true));

const passwordId = ref(null);
const loading = ref(false);
const displayErrorMessage = ref(false);
const backendErrorMessage = ref(null);
const lastActionTriggered = ref(null);

watch(show, (value) => {
    clearForm();
    if (!value) {
        passwordId.value = null;
        displayErrorMessage.value = false;
        backendErrorMessage.value = null;
    }
});

const requestUpdatePassword = (options) => {
    backendApi()
        .users()
        .changePassword({
            id: passwordId.value,
            password: options.password
        })
        .then((response) => {
            if (response) {
                clearForm();
                passwordId.value = null;
                displayErrorMessage.value = false;
                backendErrorMessage.value = null;
                close();
                emit('saved');
            }
        })
        .catch((error) => {
            displayErrorMessage.value = true;
            messageErrors.value = error.data?.message;
        })
        .finally(() => {
            loading.value = false;
        });
};

const open = async (id) => {
    show.value = true;
    if (id) {
        lastActionTriggered.value = 'get';
        passwordId.value = id;
    }
};

const onSaveBtnPressed = () => {
    onValidatedSubmit((options) => {
        loading.value = true;
        if (passwordId.value) {
            lastActionTriggered.value = 'update';
            requestUpdatePassword(options);
        }
    });
};

const onRetryBtnPressed = () => {
    displayErrorMessage.value = false;
    if (lastActionTriggered.value === 'update') {
        loading.value = true;
        requestUpdatePassword({
            id: passwordId.value,
            password: fieldModels['password'].value.value
        });
    }
};

defineExpose({
    open,
    close
});
</script>

<template>
    <CreationDialog
        v-model="show"
        :page="$t('User')"
        :title="'Edit'"
        :width="1000"
        :compact-header-margin="true"
        :hideEditAction="true"
        :hideNewAction="true"
        :hideRemoveAction="true"
        :saving="loading"
        @save="onSaveBtnPressed"
    >
        <template v-slot:form>
            <RetryFailedRequest
                v-if="displayErrorMessage"
                :message="backendErrorMessage"
                @retry="onRetryBtnPressed"
            ></RetryFailedRequest>

            <ErrorMessage
                :show="messageErrors.length > 0"
                :message="messageErrors.join('\n')"
            ></ErrorMessage>

            <section
                v-for="(section, index) in passwordFormTemplate"
                :key="section.title"
            >
                <FormSectionTitle
                    :title="section.title"
                    v-model="sectionVisibilities[index]"
                />
                <transition name="slide">
                    <v-row v-show="sectionVisibilities[index]" class="mt-3">
                        <FormLine
                            v-for="field of section.fields"
                            :key="field.label"
                            :fullWidth="field.fullWidth"
                            :oneColumn="field.oneColumn"
                            :label="field.label"
                            :mode="field.disabled ? '' : 'edit'"
                            :component="field.component"
                            :fieldModels="fieldModels"
                            :error-messages="
                                fieldModels[field.model].errorMessage.value
                            "
                            v-model="fieldModels[field.model].value.value"
                            :required="field.required"
                            :model-name="field.model"
                            :align="field.align"
                            :return-object="field.returnObject"
                            @onChange="onFiledChanged"
                            @update:modelValue="onUpdateFieldValue"
                        ></FormLine>
                    </v-row>
                </transition>
            </section>
        </template>
    </CreationDialog>
</template>

<style lang="scss" scoped>
section {
    margin-bottom: 3rem;
}
</style>
